import { useEffect, useMemo, useRef, useState } from 'react';
import './SlotMachineStrip.css';

const EVENT_MODELS = [
  {
    id: 'festivals',
    label: 'Festivals',
    eyebrow: 'Multi-day capacity',
    image: '/1_festivals.jpg',
    accent: '#6EE7FF',
    gradient: 'linear-gradient(135deg, #6EE7FF, #7FA8FF)',
    metrics: ['Multi-day passes', 'Camping add-ons', 'Stage access'],
    signal: 'High-volume entry with layered ticket types.',
  },
  {
    id: 'corporate',
    label: 'Corporate',
    eyebrow: 'Guest orchestration',
    image: '/2_corporate.jpg',
    accent: '#B482FF',
    gradient: 'linear-gradient(135deg, #B482FF, #6EE7FF)',
    metrics: ['Invite flows', 'Check-in groups', 'Networking tiers'],
    signal: 'Controlled access for teams, partners and guests.',
  },
  {
    id: 'venues',
    label: 'Venues',
    eyebrow: 'Calendar infrastructure',
    image: '/3_venues.jpg',
    accent: '#6EE7FF',
    gradient: 'linear-gradient(135deg, #6EE7FF, #22C55E)',
    metrics: ['Recurring shows', 'Reserved inventory', 'Door validation'],
    signal: 'A repeatable operating layer for every night.',
  },
  {
    id: 'sports',
    label: 'Sport Clubs',
    eyebrow: 'Season-ready',
    image: '/4_sportclubs.jpg',
    accent: '#B482FF',
    gradient: 'linear-gradient(135deg, #7FA8FF, #B482FF)',
    metrics: ['Season tickets', 'Seat maps', 'Fan memberships'],
    signal: 'Structured seat, QR and settlement logic for clubs.',
  },
  {
    id: 'expositions',
    label: 'Expositions',
    eyebrow: 'Timed attendance',
    image: '/5_conferences.jpg',
    accent: '#6EE7FF',
    gradient: 'linear-gradient(135deg, #6EE7FF, #A8DDFF)',
    metrics: ['Sessions', 'Workshops', 'Timed access'],
    signal: 'Segmented flows for visitors, speakers and crew.',
  },
  {
    id: 'clubs',
    label: 'Club Nights',
    eyebrow: 'Fast-cycle revenue',
    image: '/6_clubnights.jpg',
    accent: '#B482FF',
    gradient: 'linear-gradient(135deg, #B482FF, #FFB86B)',
    metrics: ['VIP access', 'Recurring nights', 'Memberships'],
    signal: 'Rapid launches with premium guest-list control.',
  },
];

export default function SlotMachineStrip() {
  const [activeId, setActiveId] = useState(EVENT_MODELS[3].id);
  const [hasEntered, setHasEntered] = useState(false);
  const sectionRef = useRef(null);

  const activeModel = useMemo(
    () => EVENT_MODELS.find((model) => model.id === activeId) || EVENT_MODELS[0],
    [activeId]
  );

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setHasEntered(true);
      },
      { threshold: 0.28 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!hasEntered) return undefined;
    const timer = setInterval(() => {
      setActiveId((current) => {
        const index = EVENT_MODELS.findIndex((model) => model.id === current);
        return EVENT_MODELS[(index + 1) % EVENT_MODELS.length].id;
      });
    }, 3600);

    return () => clearInterval(timer);
  }, [hasEntered]);

  return (
    <section className={`sms-section ${hasEntered ? 'sms-section--entered' : ''}`} ref={sectionRef}>
      <div className="boundary-marker boundary-marker--light" aria-hidden="true" />
      <div className="sms-inner" style={{ '--active-accent': activeModel.accent }}>
        <div className="sms-header">
          <p className="sms-eyebrow">WHO WE SERVE</p>
          <h2 className="sms-title">
            One operating layer.
            <span className="sms-title-accent"> Every event model.</span>
          </h2>
          <p className="sms-copy">
            Tick-etc. adapts ticketing, payments and access control to the way your event actually runs.
          </p>
        </div>

        <div className="sms-command">
          <div className="sms-command-panel">
            <div className="sms-panel-topline">
              <span>Event OS</span>
              <span>Adaptive structure</span>
            </div>
            <div className="sms-active-card" style={{ '--active-image': `url(${activeModel.image})` }}>
              <div className="sms-active-media" />
              <div className="sms-active-content">
                <span className="sms-active-eyebrow">{activeModel.eyebrow}</span>
                <h3>{activeModel.label}</h3>
                <p>{activeModel.signal}</p>
                <div className="sms-active-metrics">
                  {activeModel.metrics.map((metric) => (
                    <span key={metric}>{metric}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="sms-system-readout">
              <div>
                <span className="sms-readout-label">Structure</span>
                <strong>Ticketing + payments + access</strong>
              </div>
              <div>
                <span className="sms-readout-label">Status</span>
                <strong>Ready to launch</strong>
              </div>
            </div>
          </div>

          <div className="sms-model-grid" role="list" aria-label="Event models">
            {EVENT_MODELS.map((model, index) => {
              const isActive = model.id === activeId;
              return (
                <button
                  key={model.id}
                  type="button"
                  className={`sms-model ${isActive ? 'sms-model--active' : ''}`}
                  style={{
                    '--model-accent': model.accent,
                    '--model-gradient': model.gradient,
                    '--model-delay': `${index * 70}ms`,
                  }}
                  onMouseEnter={() => setActiveId(model.id)}
                  onFocus={() => setActiveId(model.id)}
                >
                  <span className="sms-model-index">{String(index + 1).padStart(2, '0')}</span>
                  <span className="sms-model-main">
                    <span className="sms-model-label">{model.label}</span>
                    <span className="sms-model-sub">{model.eyebrow}</span>
                  </span>
                  <span className="sms-model-pulse" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
